/**
 * Centralised RBAC permission helpers.
 * Role hierarchy: super_admin > tenant_admin > ast_admin > supervisor > agent > pbx_user
 */

export const ROLES = {
  SUPER_ADMIN:  'super_admin',
  TENANT_ADMIN: 'tenant_admin',
  AST_ADMIN:    'ast_admin',
  SUPERVISOR:   'supervisor',
  AGENT:        'agent',
  PBX_USER:     'pbx_user',
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

// ── Primary role checks ──────────────────────────────────────────

export function isPlatformAdmin(user: any): boolean {
  return !!user?.is_admin || user?.role === ROLES.SUPER_ADMIN;
}
export function isTenantAdmin(user: any): boolean {
  return user?.role === ROLES.TENANT_ADMIN;
}
export function isAdmin(user: any): boolean {
  return isPlatformAdmin(user) || isTenantAdmin(user) || user?.role === 'pbx_admin';
}
export function isAsteriskAdmin(user: any): boolean {
  return user?.role === ROLES.AST_ADMIN;
}
export function isSupervisor(user: any): boolean {
  return user?.role === ROLES.SUPERVISOR
    || user?.user_type === 'supervisor'
    || !!user?.supervisor_enabled
    || !!user?.feature_supervisor;
}
export function isAgent(user: any): boolean {
  return user?.role === ROLES.AGENT;
}
export function isPbxUser(user: any): boolean {
  return user?.role === ROLES.PBX_USER;
}
export function isContactCenterUser(user: any): boolean {
  return isAgent(user)
    || isSupervisor(user)
    || !!user?.cc_enabled
    || !!user?.queue_enabled;
}
export function hasRole(user: any, ...roles: Role[]): boolean {
  return roles.some(r => user?.role === r);
}

// ── Role labels for UI display ───────────────────────────────────

export function getRoleLabel(user: any): string {
  if (isPlatformAdmin(user))  return 'Platform Admin';
  if (user?.role === 'pbx_admin') return 'PBX Administrator';
  if (isTenantAdmin(user))    return 'Tenant Admin';
  if (isAsteriskAdmin(user))  return 'PBX Admin';
  if (isSupervisor(user))     return 'Supervisor';
  if (isAgent(user))          return 'CC Agent';
  if (isPbxUser(user))        return 'PBX User';
  return 'User';
}

export function getWorkspaceLabel(user: any): string {
  if (isSupervisor(user)) return 'Supervisor';
  if (isAgent(user))      return 'Agent Workspace';
  return 'Softphone';
}

// ── Feature flag keys ────────────────────────────────────────────

export type Feature =
  | 'recording'
  | 'contacts'
  | 'from_number'
  | 'recent_calls'
  | 'settings'
  | 'transfer'
  | 'queue_mode'
  | 'supervisor'
  | 'wallboard'
  | 'crm_popup'
  | 'ai_transcription';

// Features CC agents get by default (without explicit feature flags)
const AGENT_FEATURES: Feature[] = [
  'recording', 'contacts', 'from_number', 'recent_calls',
  'settings', 'transfer', 'queue_mode', 'crm_popup', 'ai_transcription',
];

// Supervisors get all agent features plus monitoring tools
const SUPERVISOR_FEATURES: Feature[] = [
  'recording', 'contacts', 'from_number', 'recent_calls',
  'settings', 'transfer', 'queue_mode', 'crm_popup', 'ai_transcription',
  'supervisor', 'wallboard',
];

const AGENT_SET = new Set<Feature>(AGENT_FEATURES);
const SUPERVISOR_SET = new Set<Feature>(SUPERVISOR_FEATURES);

/**
 * Returns true if the user has the given feature enabled.
 * - Admins: always true
 * - Supervisors: full set (including wallboard, supervisor tools)
 * - CC Agents: base CC feature set
 * - PBX Users: only explicit feature_* flags on the user object
 */
export function can(user: any, feature: Feature): boolean {
  if (!user) return false;
  if (isAdmin(user)) return true;
  // Explicit false from admin ALWAYS overrides role defaults
  const explicit = (user as any)[`feature_${feature}`];
  if (explicit === false || explicit === 0) return false;
  if (isSupervisor(user)) return SUPERVISOR_SET.has(feature) || !!explicit;
  if (isAgent(user))      return AGENT_SET.has(feature)      || !!explicit;
  // PBX User: explicit flag only
  return !!explicit;
}

// ── Shell routing ────────────────────────────────────────────────

export function getShell(user: any): 'admin' | 'asterisk_admin' | 'supervisor' | 'workspace' {
  if (isAdmin(user))         return 'admin';
  if (isAsteriskAdmin(user)) return 'asterisk_admin';
  if (isSupervisor(user))    return 'supervisor';
  return 'workspace';
}
